/**
 * Binary-search lookups over a list sorted ascending by a numeric key. Callers own the sort
 * order; these helpers only assume it, keeping per-frame selectors sublinear on large lists.
 */

/** Index of the first item whose key is >= `target` (lower bound). */
export function lowerBoundBy<T>(
  sortedItems: readonly T[],
  target: number,
  keyOf: (item: T) => number,
): number {
  let low = 0;
  let high = sortedItems.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    const item = sortedItems[middle];
    if (item !== undefined && keyOf(item) < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

/**
 * Item whose key is closest to `target`. An equal-distance tie within the float comparison
 * epsilon resolves toward the later item; same-key runs resolve to the first of the run
 * (the lower bound).
 */
export function findNearestBy<T>(
  sortedItems: readonly T[],
  target: number,
  keyOf: (item: T) => number,
): T | undefined {
  const nextIndex = lowerBoundBy(sortedItems, target, keyOf);
  const next = sortedItems[nextIndex];
  const previous = nextIndex > 0 ? sortedItems[nextIndex - 1] : undefined;
  if (next === undefined) return previous;
  if (previous === undefined) return next;

  const nextKey = keyOf(next);
  const previousKey = keyOf(previous);
  const nextDistance = nextKey - target;
  const previousDistance = target - previousKey;
  const comparisonEpsilon =
    Number.EPSILON * Math.max(1, Math.abs(target), Math.abs(nextKey), Math.abs(previousKey)) * 8;
  return nextDistance - previousDistance > comparisonEpsilon ? previous : next;
}

/** Every item whose key is within `epsilon` of `target`, bounds inclusive. */
export function findAllWithinBy<T>(
  sortedItems: readonly T[],
  target: number,
  epsilon: number,
  keyOf: (item: T) => number,
): T[] {
  const matches: T[] = [];
  for (
    let index = lowerBoundBy(sortedItems, target - epsilon, keyOf);
    index < sortedItems.length;
    index += 1
  ) {
    const item = sortedItems[index];
    if (item === undefined || keyOf(item) > target + epsilon) break;
    matches.push(item);
  }
  return matches;
}
